import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const pillars = [
  {
    k: "Generate",
    body: "Content, code & domain-specific knowledge — composed by intelligent creation pipelines.",
  },
  {
    k: "Understand",
    body: "Context-aware, multilingual reasoning systems that consume and structure knowledge.",
  },
  {
    k: "Act",
    body: "Autonomous agents and orchestration that turn intent into reliable execution.",
  },
];

export default function VisionSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yBig = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.4, 0.8, 1], [0, 1, 1, 0.5]);

  return (
    <section
      ref={ref}
      id="vision"
      data-testid="vision-section"
      className="relative w-full py-40 md:py-56 overflow-hidden"
    >
      {/* Background image masked */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/49232ec2-aae0-423c-8239-d1d817731aa4/images/acd51d886aec6992570645ceb3bb8b098530aba2bc3cc6d1740778afb4bb588b.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, transparent 70%)",
        }}
      />

      <div className="relative w-full max-w-screen-2xl mx-auto px-6 md:px-12 xl:px-24">
        <div className="flex items-center gap-3 mb-12 md:mb-16">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#d4ff00]">
            /03
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#8a8a8a]">
            Direction
          </span>
        </div>

        <motion.h2
          style={{ y: yBig, opacity }}
          className="font-display uppercase tracking-tightest font-medium text-white leading-[0.88] max-w-[15ch]"
          data-testid="vision-headline"
        >
          <span style={{ fontSize: "clamp(2.6rem, 9vw, 10rem)" }} className="block">
            A sovereign
          </span>
          <span
            style={{ fontSize: "clamp(2.6rem, 9vw, 10rem)" }}
            className="block text-[#4a4a4a]"
          >
            intelligence
          </span>
          <span style={{ fontSize: "clamp(2.6rem, 9vw, 10rem)" }} className="block">
            from <span className="text-[#d4ff00]">India</span>.
          </span>
        </motion.h2>

        <div className="mt-24 md:mt-40 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4 md:col-start-7">
            <p className="text-base md:text-lg text-[#8a8a8a] leading-relaxed font-light">
              We build systems that think with us — autonomous, reliable and
              scalable. Not theory; deployed intelligence woven into real-world
              environments. A long arc toward generally capable AI, written
              from where we stand.
            </p>
          </div>
        </div>

        <div className="mt-32 md:mt-48 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-white/10">
          {pillars.map((p, i) => (
            <motion.div
              key={p.k}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 1, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`relative py-10 md:py-14 px-0 md:px-10 ${
                i !== 0 ? "md:border-l border-white/10" : ""
              } border-t md:border-t-0 border-white/10`}
              data-testid={`pillar-${p.k.toLowerCase()}`}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#4a4a4a]">
                Pillar / 0{i + 1}
              </span>
              <h3
                className="mt-3 font-display tracking-tight text-white"
                style={{ fontSize: "clamp(1.8rem, 3.4vw, 3rem)" }}
              >
                {p.k}.
              </h3>
              <p className="mt-4 text-sm md:text-base text-[#8a8a8a] font-light leading-relaxed max-w-xs">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
